import { useState, useEffect, useCallback } from 'react';
import { isClause4Active } from '../utils/rtcco';

const REFERENCE_PRICE      = 1.90;
const DEFAULT_CONSUMPTION  = 11;   // L/100km
const DEFAULT_DAYS         = 20;
const GOODWILL_RATE        = 0.384;

const STORE_KM      = 'hp-fuel-route-km';
const STORE_DAYS    = 'hp-fuel-working-days';
const STORE_L100    = 'hp-fuel-consumption';
const STORE_GOODWILL= 'hp-fuel-goodwill';
const STORE_AIP     = 'hp-fuel-aip-price';       // periodEnd → entered AIP/bowser price
const STORE_WINDOW  = 'hp-fuel-highprice-days';  // periodEnd → # days AIP was above the $2 trigger
const STORE_EXCLUDE = 'hp-fuel-excluded';        // login → true: removed from the workbench list
const PERIOD_DAYS   = 28;
const TRIGGER_PRICE = 2.00;                      // RTCCO clause 4 diesel trigger

// Period-end dates generated from the 28-day anchor (same cadence as the other
// pages) so the list always includes the current/just-ended period — no hardcoding.
const FUEL_PERIOD_ANCHOR = '2026-05-16';   // a known period-END date
function buildFuelPeriods() {
  const STEP = 28, dayMs = 86400000;
  const anchor = new Date(FUEL_PERIOD_ANCHOR + 'T12:00:00');
  const today = new Date();
  const toISO = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // k = steps from anchor to the current (in-progress) period end (>= today)
  const k = Math.ceil((today.getTime() - anchor.getTime()) / (STEP * dayMs));
  const out = [];
  for (let i = k; i >= k - 15; i--) {              // current period + 15 prior
    const d = new Date(anchor); d.setDate(anchor.getDate() + STEP * i);
    out.push(toISO(d));
  }
  return out;
}
const KNOWN_PERIODS = buildFuelPeriods();

// A fuel period is 28 days ending on the selected date → start = end − 27 (inclusive).
function periodStartOf(isoEnd) {
  const d = new Date(isoEnd + 'T12:00:00');
  d.setDate(d.getDate() - 27);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Start of the last-N-days window of a period (inclusive of the end date).
function windowStartOf(isoEnd, nDays) {
  const d = new Date(isoEnd + 'T12:00:00');
  d.setDate(d.getDate() - (nDays - 1));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Default to the most-recently-COMPLETED period (latest end on/before today).
function defaultFuelPeriod() {
  const todayISO = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  return KNOWN_PERIODS.find(p => p <= todayISO) || KNOWN_PERIODS[0];
}

// The period before a period end (28-day cadence).
function prevPeriodOf(isoEnd) {
  const d = new Date(isoEnd + 'T12:00:00');
  d.setDate(d.getDate() - 28);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function loadMap(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
}
function saveMap(key, map) { localStorage.setItem(key, JSON.stringify(map)); }

function fmt$(n) {
  if (!n && n !== 0) return '$0.00';
  const num = parseFloat(n);
  const abs = Math.abs(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return num < 0 ? '-$' + abs : '$' + abs;
}

function periodLabel(isoStr) {
  if (!isoStr) return isoStr;
  const end = new Date(isoStr + 'T12:00:00');
  const start = new Date(end); start.setDate(end.getDate() - 27);
  const f = d => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${f(start)} to ${f(end)}`;
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', borderTop: `4px solid ${accent}` }}>
      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: '800', color: accent, margin: '8px 0 4px', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

function MsgBanner({ msg }) {
  if (!msg) return null;
  const ok = msg.type === 'success';
  return (
    <div style={{ background: ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${ok ? '#86efac' : '#fca5a5'}`, borderRadius: '8px', padding: '12px 16px', color: ok ? '#166534' : '#dc2626', fontWeight: '600', fontSize: '14px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
      <span>{msg.text}</span>
      {msg.rctiLink && (
        <a href="/rcti" style={{ padding: '6px 14px', borderRadius: '6px', background: '#064e3b', color: '#fff', fontSize: '13px', fontWeight: '700', textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Build RCTIs →
        </a>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = { Pending: ['#fefce8','#92400e'], Confirmed: ['#f0fdf4','#065f46'], Void: ['#f3f4f6','#6b7280'], Carried: ['#eff6ff','#1e40af'] };
  const [bg, fg] = map[status] || ['#f3f4f6','#6b7280'];
  return <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:bg, color:fg }}>{status}</span>;
}

// Inline-editable number cell — click to edit, blur/Enter to save
function EditCell({ login, value, editState, setEditState, onSave, suffix = '', step = '1', minWidth = '52px' }) {
  const draft = editState[login];
  const isEditing = draft !== undefined;
  if (isEditing) {
    return (
      <input
        type="number" min="0" step={step} autoFocus
        value={draft}
        onChange={e => setEditState(p => ({ ...p, [login]: e.target.value }))}
        onBlur={e => { onSave(login, e.target.value); setEditState(p => { const n={...p}; delete n[login]; return n; }); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { onSave(login, draft); setEditState(p => { const n={...p}; delete n[login]; return n; }); }
          if (e.key === 'Escape') { setEditState(p => { const n={...p}; delete n[login]; return n; }); }
        }}
        style={{ width: minWidth, padding: '3px 6px', borderRadius: '5px', border: '2px solid #064e3b', fontSize: '12px', textAlign: 'right' }}
      />
    );
  }
  return (
    <span
      onClick={() => setEditState(p => ({ ...p, [login]: String(value) }))}
      title="Click to edit"
      style={{ cursor: 'pointer', color: value > 0 ? '#374151' : '#d1d5db', fontWeight: value > 0 ? '600' : '400', borderBottom: '1px dashed #d1d5db', paddingBottom: '1px', whiteSpace: 'nowrap' }}
    >
      {value > 0 ? `${value}${suffix}` : '— set'}
    </span>
  );
}

// ── Fuel Workbench Tab ────────────────────────────────────────────────────────

function FuelWorkbenchTab() {
  const [periodEnd,      setPeriodEnd]      = useState(defaultFuelPeriod());
  const [aipPrice,       setAipPrice]       = useState(() => loadMap(STORE_AIP)[defaultFuelPeriod()] ?? '');
  const [windowMap,      setWindowMap]      = useState(() => loadMap(STORE_WINDOW));
  const [routeKmMap,     setRouteKmMap]     = useState(() => loadMap(STORE_KM));
  const [actualKm,       setActualKm]       = useState({}); // login → { totalKm, days } from approved shift logs
  const [workingDaysMap, setWorkingDaysMap] = useState(() => loadMap(STORE_DAYS));
  const [consumptionMap, setConsumptionMap] = useState(() => loadMap(STORE_L100));
  const [editKm,         setEditKm]         = useState({});
  const [editDays,       setEditDays]       = useState({});
  const [editL100,       setEditL100]       = useState({});
  const [goodwillOn,     setGoodwillOn]     = useState(() => localStorage.getItem(STORE_GOODWILL) === 'true');
  const [aipFetched,     setAipFetched]     = useState(null);
  const [aipFetching,    setAipFetching]    = useState(false);
  const [aipFetchErr,    setAipFetchErr]    = useState(null);
  const [drivers,        setDrivers]        = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driverError,    setDriverError]    = useState(null);
  const [staged,         setStaged]         = useState([]);
  const [stagingFor,     setStagingFor]     = useState(null);
  const [stagingMsg,     setStagingMsg]     = useState(null);
  const [confirmingAll,  setConfirmingAll]  = useState(false);
  const [confirmMsg,     setConfirmMsg]     = useState(null);
  const [showAll,        setShowAll]        = useState(false);
  const [excludedMap,    setExcludedMap]    = useState(() => loadMap(STORE_EXCLUDE));
  const [showExcluded,   setShowExcluded]   = useState(false);
  const [stagingAll,     setStagingAll]     = useState(false);
  const [bulkDays,       setBulkDays]       = useState('');
  // RTCCO clause 4 (fuel pass-through) status, read from the MSO registry.
  // When suspended, price tracking stays live (audit trail) but staging /
  // confirming / pushing to RCTI is disabled — no pass-through obligation.
  const [clause4Active] = useState(isClause4Active);
  // Last period's fuel that was staged but never confirmed (so never reached an RCTI) —
  // offered for carrying into this period as a catch-up. See fuel-adjustments-sb.
  const [prevUnpaid,     setPrevUnpaid]     = useState([]);
  const [carrying,       setCarrying]       = useState(false);
  // Attendance check: who actually drove in the calculation window (shift log or AP scans)
  // and who was on approved leave — so holiday days aren't paid fuel.
  const [attendance,     setAttendance]     = useState(null);   // { key, from, to, drivers, leave }
  const [checkingAtt,    setCheckingAtt]    = useState(false);
  const [applyingAtt,    setApplyingAtt]    = useState(false);

  const loadDrivers = useCallback(async () => {
    setLoadingDrivers(true); setDriverError(null);
    try {
      const res = await fetch('/.netlify/functions/sheets');
      const json = await res.json();
      if (json.success) setDrivers((json.drivers || []).filter(d => d.active !== false));
      else setDriverError(json.error || 'Failed to load drivers');
    } catch (err) { setDriverError(err.message); }
    finally { setLoadingDrivers(false); }
  }, []);

  const loadStaged = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/fuel-adjustments-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'getFuelAdjustments', period: periodEnd }),
      });
      const json = await res.json();
      if (json.success) setStaged(json.adjustments || []);
    } catch {}
    try {
      const res = await fetch('/.netlify/functions/fuel-adjustments-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'getFuelAdjustments', period: prevPeriodOf(periodEnd) }),
      });
      const json = await res.json();
      setPrevUnpaid(json.success ? (json.adjustments || []).filter(a => a.status === 'Pending' && !a.catchUpFrom && a.adjustment > 0) : []);
    } catch { setPrevUnpaid([]); }
  }, [periodEnd]);

  // Days (at the END of the period) the AIP price sat above the $2.00 trigger.
  // 0 / blank = the whole period. When set, actual km & days are queried for that
  // window only, so the adjustment covers exactly the high-price days.
  const highDays = Math.min(PERIOD_DAYS, parseInt(windowMap[periodEnd]) || 0);
  const windowActive = highDays > 0 && highDays < PERIOD_DAYS;
  const kmFrom = windowActive ? windowStartOf(periodEnd, highDays) : periodStartOf(periodEnd);

  // Actual km per driver over the calculation window (whole period, or the last-N
  // high-price days), summed from APPROVED shift logs (odometer end − start).
  // Keyed by LOGIN → { totalKm, days }; a driver with no approved km simply isn't
  // here (→ km 0, flagged).
  const loadActualKm = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/fuel-adjustments-sb', {
        method: 'POST',
        body: JSON.stringify({ action: 'getDriverActualKm', from: kmFrom, to: periodEnd }),
      });
      const json = await res.json();
      setActualKm(json.success ? (json.drivers || {}) : {});
    } catch { setActualKm({}); }
  }, [kmFrom, periodEnd]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);
  useEffect(() => { loadStaged();  }, [loadStaged]);
  useEffect(() => { loadActualKm(); }, [loadActualKm]);

  // Changing period recalls that period's saved AIP price (entered price persists per period).
  useEffect(() => { setAipPrice(loadMap(STORE_AIP)[periodEnd] ?? ''); }, [periodEnd]);
  const updateAipPrice = v => {
    setAipPrice(v);
    const m = loadMap(STORE_AIP); m[periodEnd] = v; saveMap(STORE_AIP, m);
  };
  const updateHighDays = v => {
    const m = { ...windowMap };
    const n = parseInt(v);
    if (n > 0) m[periodEnd] = Math.min(PERIOD_DAYS, n); else delete m[periodEnd];
    setWindowMap(m); saveMap(STORE_WINDOW, m);
  };

  // Per-driver lookups. Precedence: a manual override (inline edit) always wins; else the
  // driver's ACTUAL captured km/day for the period; else 0 (flagged for manual entry — we
  // no longer fall back to the old flat 50 km/day estimate).
  const actualPerDay = login => { const a = actualKm[login]; return (a && a.days > 0) ? Math.round(a.totalKm / a.days) : undefined; };
  const getKm   = login => routeKmMap[login]     ?? actualPerDay(login)    ?? 0;
  const getDays = login => workingDaysMap[login]  ?? (actualKm[login]?.days) ?? (windowActive ? Math.min(DEFAULT_DAYS, highDays) : DEFAULT_DAYS);
  const getL100 = login => consumptionMap[login]  ?? DEFAULT_CONSUMPTION;

  // Persist map updates
  const saveKm   = (login, v) => { const m={...routeKmMap,   [login]:parseFloat(v)||0}; setRouteKmMap(m);     saveMap(STORE_KM,   m); };
  const saveDays = (login, v) => { const m={...workingDaysMap,[login]:parseInt(v)  ||DEFAULT_DAYS}; setWorkingDaysMap(m); saveMap(STORE_DAYS, m); };
  const saveL100 = (login, v) => { const m={...consumptionMap,[login]:parseFloat(v)||DEFAULT_CONSUMPTION}; setConsumptionMap(m); saveMap(STORE_L100, m); };

  const toggleGoodwill = val => { setGoodwillOn(val); localStorage.setItem(STORE_GOODWILL, val); };

  const price      = parseFloat(aipPrice) || 0;
  const priceDelta = price - REFERENCE_PRICE;

  // Build display rows
  // This period's own fuel vs catch-ups carried in from an earlier period. The driver
  // table, cards and "agreed" line are this period's own; catch-ups get their own panel.
  const ownStaged      = staged.filter(s => !s.catchUpFrom);
  const catchUps       = staged.filter(s => s.catchUpFrom);
  const catchUpActive  = catchUps.filter(s => s.status === 'Pending' || s.status === 'Confirmed');
  const catchUpTotal   = catchUpActive.reduce((s, a) => s + (a.adjustment || 0), 0);
  const prevUnpaidTotal = prevUnpaid.reduce((s, a) => s + (a.adjustment || 0), 0);
  const rows = drivers.map(d => {
    const login  = (d.loginName || '').toUpperCase();
    const km     = getKm(login);
    const l100   = getL100(login);
    const days   = getDays(login);
    const fuelL  = km * (l100 / 100) * days;
    const fuelAdj    = price > 0 && km > 0 ? Math.round(priceDelta * fuelL * 100) / 100 : 0;
    const goodwillAmt= goodwillOn ? Math.round(fuelAdj * GOODWILL_RATE * 100) / 100 : 0;
    const totalAdj   = Math.round((fuelAdj + goodwillAmt) * 100) / 100;
    const stagedEntry= ownStaged.find(s => s.driver === login && (s.status === 'Pending' || s.status === 'Confirmed'));
    const a = actualKm[login];
    const kmSource = routeKmMap[login] != null ? 'manual' : (a && a.days > 0 && a.totalKm > 0) ? 'actual' : 'none';
    return { login, name: d.name || login, company: d.companyName || '', km, l100, days, fuelL, fuelAdj, goodwillAmt, totalAdj, staged: stagedEntry || null,
             kmSource, actualTotalKm: a ? a.totalKm : null, actualDays: a ? a.days : null, excluded: !!excludedMap[login] };
  });

  // Removed drivers drop out of every list and total; they only render (greyed,
  // with a Restore button) when "Show removed" is ticked.
  const activeRows     = rows.filter(r => !r.excluded);
  const excludedRows   = rows.filter(r => r.excluded);
  const withKm         = activeRows.filter(r => r.km > 0);
  const displayRows    = [...(showAll ? activeRows : withKm), ...(showExcluded ? excludedRows : [])];
  const noKmCount      = activeRows.filter(r => r.km === 0).length;
  const pendingCount   = staged.filter(s => s.status === 'Pending').length;   // incl. catch-ups: Confirm All covers both
  const confirmedCount = ownStaged.filter(s => s.status === 'Confirmed').length;
  const totalAdj       = withKm.reduce((s, r) => s + Math.max(0, r.totalAdj), 0);
  const confirmedTotal = ownStaged.filter(s => s.status === 'Confirmed').reduce((s, a) => s + (a.adjustment || 0), 0);
  const eligibleToStage = displayRows.filter(r => !r.excluded && price > REFERENCE_PRICE && r.km > 0 && r.totalAdj > 0 && !r.staged);
  const agreedActive    = ownStaged.filter(s => s.status === 'Pending' || s.status === 'Confirmed');
  const agreedTotal     = agreedActive.reduce((s, a) => s + (a.adjustment || 0), 0);
  const agreedCount     = agreedActive.length;

  // Stage every eligible driver in one call (select-all).
  async function stageAll() {
    if (!clause4Active) { setStagingMsg({ type: 'error', text: 'RTCCO clause 4 is suspended — fuel pass-through is disabled. Reactivate on the MSO Tracker when the diesel trigger is met.' }); return; }
    if (!eligibleToStage.length) { setStagingMsg({ type: 'error', text: 'No eligible drivers to stage.' }); return; }
    setStagingAll(true); setStagingMsg(null);
    try {
      const rowsPayload = eligibleToStage.map(r => ({
        driver: r.login, driverName: r.name, company: r.company,
        aipPrice: price, referencePrice: REFERENCE_PRICE, routeKm: r.km, workingDays: r.days, consumption: r.l100,
        totalFuelL: Math.round(r.fuelL * 100) / 100, fuelAdj: r.fuelAdj,
        goodwillRate: goodwillOn ? GOODWILL_RATE : 0, goodwillAmount: r.goodwillAmt, adjustment: r.totalAdj,
        notes: `RTCCO Fuel — AIP $${price.toFixed(3)}/L${windowActive ? ` · >$${TRIGGER_PRICE.toFixed(2)} last ${highDays} days` : ''}${goodwillOn ? ' + 38.4% goodwill' : ''}`, appliedBy: 'paul@hollypaul.com',
      }));
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'stageFuelAdjustmentsBulk', period: periodEnd, rows: rowsPayload }) });
      const json = await res.json();
      if (json.success) { setStagingMsg({ type: 'success', text: `✓ Staged ${json.staged} driver${json.staged !== 1 ? 's' : ''}${json.skipped ? ` · ${json.skipped} already staged` : ''}` }); await loadStaged(); }
      else setStagingMsg({ type: 'error', text: json.error || 'Stage all failed' });
    } catch (err) { setStagingMsg({ type: 'error', text: err.message }); }
    finally { setStagingAll(false); }
  }

  // Apply one "days worked" value to every driver (removed drivers untouched).
  function applyDaysToAll() {
    const days = parseInt(bulkDays) || (windowActive ? highDays : DEFAULT_DAYS);
    const m = { ...workingDaysMap };
    activeRows.forEach(r => { m[r.login] = days; });
    setWorkingDaysMap(m); saveMap(STORE_DAYS, m);
    setStagingMsg({ type: 'success', text: `✓ Set ${days} days worked for all ${activeRows.length} drivers` });
  }

  // Remove a driver from the workbench list (persisted), or bring one back.
  // Drivers with an active staged adjustment must be voided first — the remove
  // button is hidden for them so the Void path can't disappear.
  function toggleExclude(login) {
    const m = { ...excludedMap };
    const removing = !m[login];
    if (removing) m[login] = true; else delete m[login];
    setExcludedMap(m); saveMap(STORE_EXCLUDE, m);
    setStagingMsg({ type: 'success', text: removing
      ? `✓ ${login} removed from the fuel list — tick "Show removed" to restore`
      : `✓ ${login} restored to the fuel list` });
  }

  // ── Attendance check ──────────────────────────────────────────────────────────
  // Compares the days each driver is being paid fuel for against the days they
  // actually logged on (shift log or AP scanner) in the calculation window, and names
  // approved leave in the gap. A staged row is judged on the days it was STAGED with.
  const attKey = `${kmFrom}|${periodEnd}`;
  const att = attendance && attendance.key === attKey ? attendance : null;
  async function runAttendanceCheck() {
    setCheckingAtt(true); setStagingMsg(null);
    try {
      const [aRes, lRes] = await Promise.all([
        fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'getDriverAttendance', from: kmFrom, to: periodEnd }) }).then(r => r.json()),
        fetch('/.netlify/functions/roster-data').then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      if (!aRes.success) { setStagingMsg({ type: 'error', text: aRes.error || 'Attendance check failed' }); return; }
      setAttendance({ key: attKey, from: kmFrom, to: periodEnd, drivers: aRes.drivers || {}, leave: lRes ? (lRes.entries || []) : null });
    } catch (err) { setStagingMsg({ type: 'error', text: err.message }); }
    finally { setCheckingAtt(false); }
  }
  // Days the depot ran at all (anyone logged on) — leave is only counted on these, so a
  // Sunday inside a leave booking doesn't read as a missed working day.
  const depotDays = att ? new Set(Object.values(att.drivers).flatMap(d => d.dates || [])) : new Set();
  // Approved leave dates for a driver inside the window that they did NOT work.
  const leaveDatesFor = (row, workedDates) => {
    if (!att || !att.leave) return [];
    const nm = (row.name || '').trim().toLowerCase();
    const worked = new Set(workedDates);
    const out = new Set();
    att.leave.filter(e => e.status === 'approved'
        && (((e.login || '').toUpperCase() === row.login) || (nm && (e.name || '').trim().toLowerCase() === nm)))
      .forEach(e => {
        const a = e.startDate > att.from ? e.startDate : att.from;
        const b = e.endDate < att.to ? e.endDate : att.to;
        for (let d = new Date(a + 'T12:00:00'); a <= b && d <= new Date(b + 'T12:00:00'); d.setDate(d.getDate() + 1)) {
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          if (!worked.has(iso) && depotDays.has(iso)) out.add(iso);
        }
      });
    return [...out].sort();
  };
  const attendanceFor = row => {
    if (!att) return null;
    const a = att.drivers[row.login] || { attended: 0, dates: [] };
    const paidDays = row.staged ? (row.staged.workingDays || 0) : row.days;
    const over = Math.max(0, paidDays - a.attended);
    const paidAmt = row.staged ? (row.staged.adjustment || 0) : row.totalAdj;
    return { attended: a.attended, paidDays, over, leave: leaveDatesFor(row, a.dates), overAmt: paidDays > 0 ? Math.round(paidAmt * over / paidDays * 100) / 100 : 0 };
  };
  const attRows = att ? activeRows.filter(r => r.km > 0 || r.staged).map(r => ({ row: r, a: attendanceFor(r) })) : [];
  const attOver = attRows.filter(x => x.a.over > 0);
  const attOverDays = attOver.reduce((s, x) => s + x.a.over, 0);
  const attOverAmt = attOver.reduce((s, x) => s + x.a.overAmt, 0);
  const attConfirmed = attOver.filter(x => x.row.staged && x.row.staged.status === 'Confirmed');
  const attFixable = attOver.filter(x => !(x.row.staged && x.row.staged.status === 'Confirmed'));

  // Set each over-claimed driver's Days to what they attended. A Pending row was staged
  // with the old days, so it's voided (soft) to be re-staged at the corrected amount.
  // Confirmed rows are never touched here — they may already be on an RCTI.
  async function applyAttendance() {
    if (!attFixable.length) return;
    const pend = attFixable.filter(x => x.row.staged && x.row.staged.status === 'Pending');
    if (!window.confirm(`Set Days to attended days for ${attFixable.length} driver${attFixable.length !== 1 ? 's' : ''}?`
      + (pend.length ? `\n\n${pend.length} of them are already staged (Pending) with the old days — they'll be voided so you can Stage All again at the corrected amounts.` : ''))) return;
    setApplyingAtt(true); setStagingMsg(null);
    try {
      const m = { ...workingDaysMap };
      attFixable.forEach(x => { m[x.row.login] = x.a.attended; });
      setWorkingDaysMap(m); saveMap(STORE_DAYS, m);
      let voided = 0;
      for (const x of pend) {
        const res = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'voidFuelAdjustment', rowIndex: x.row.staged.rowIndex }) });
        const json = await res.json();
        if (json.success) voided++;
      }
      await loadStaged();
      setStagingMsg({ type: 'success', text: `✓ Days set to attended for ${attFixable.length} driver${attFixable.length !== 1 ? 's' : ''}`
        + (voided ? ` · ${voided} pending row${voided !== 1 ? 's' : ''} voided — click Stage All to re-stage at the corrected amounts` : '')
        + (voided < pend.length ? ` · ${pend.length - voided} could not be voided, check them` : '') });
    } catch (err) { setStagingMsg({ type: 'error', text: err.message }); }
    finally { setApplyingAtt(false); }
  }

  // Drop every manual km/day override so drivers revert to their captured odometer
  // actuals for the current window. Drivers with no captured km fall back to ⚠ 0.
  function resetToActual() {
    if (!window.confirm('Clear ALL manual km and days overrides? Every driver reverts to actual odometer data; drivers without captured km go back to 0 (⚠).')) return;
    setRouteKmMap({});     saveMap(STORE_KM,   {});
    setWorkingDaysMap({}); saveMap(STORE_DAYS, {});
    setStagingMsg({ type: 'success', text: '✓ Manual overrides cleared — km/day & days now come from approved odometer logs' });
  }

  // AIP live fetch
  async function fetchAIPRate() {
    setAipFetching(true); setAipFetchErr(null);
    try {
      const res  = await fetch('/.netlify/functions/fuel-price');
      const json = await res.json();
      if (json.success) {
        setAipFetched(json);
      } else {
        setAipFetchErr(json.error || 'Could not retrieve AIP price');
      }
    } catch (err) { setAipFetchErr(err.message); }
    finally { setAipFetching(false); }
  }

  async function stageAdjustment(row) {
    if (!clause4Active) { setStagingMsg({ type: 'error', text: 'RTCCO clause 4 is suspended — fuel pass-through is disabled.' }); return; }
    if (!price || row.km === 0 || row.totalAdj <= 0) return;
    setStagingFor(row.login); setStagingMsg(null);
    try {
      const payload = {
        action: 'stageFuelAdjustment',
        period: periodEnd,
        driver: row.login,
        driverName: row.name,
        company: row.company,
        aipPrice: price,
        referencePrice: REFERENCE_PRICE,
        routeKm: row.km,
        workingDays: row.days,
        consumption: row.l100,
        totalFuelL: Math.round(row.fuelL * 100) / 100,
        fuelAdj: row.fuelAdj,
        goodwillRate: goodwillOn ? GOODWILL_RATE : 0,
        goodwillAmount: row.goodwillAmt,
        adjustment: row.totalAdj,
        notes: `RTCCO Fuel — AIP $${price.toFixed(3)}/L${windowActive ? ` · >$${TRIGGER_PRICE.toFixed(2)} last ${highDays} days` : ''}${goodwillOn ? ' + 38.4% goodwill' : ''}`,
        appliedBy: 'paul@hollypaul.com',
      };
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify(payload) });
      const json = await res.json();
      if (json.success) { setStagingMsg({ type: 'success', text: `✓ Staged for ${row.login}` }); await loadStaged(); }
      else setStagingMsg({ type: 'error', text: json.error || 'Stage failed' });
    } catch (err) { setStagingMsg({ type: 'error', text: err.message }); }
    finally { setStagingFor(null); }
  }

  async function voidAdjustment(rowIndex) {
    setStagingMsg(null);
    try {
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'voidFuelAdjustment', rowIndex }) });
      const json = await res.json();
      if (json.success) { setStagingMsg({ type: 'success', text: '✓ Voided' }); await loadStaged(); }
      else setStagingMsg({ type: 'error', text: json.error || 'Void failed' });
    } catch (err) { setStagingMsg({ type: 'error', text: err.message }); }
  }

  // Carry last period's unconfirmed fuel into this period as catch-up rows. They land
  // Pending here; Confirm All then puts each on the RCTI as its own line. Undo = Void a
  // catch-up row (its original goes back to Pending in the earlier period).
  async function carryForward() {
    const from = prevPeriodOf(periodEnd);
    if (!window.confirm(`Carry ${prevUnpaid.length} unconfirmed fuel adjustment${prevUnpaid.length !== 1 ? 's' : ''} (${fmt$(prevUnpaidTotal)}) from ${periodLabel(from)} into ${periodLabel(periodEnd)}?\n\nEach shows on this period's RCTI as its own "catch-up" fuel line, next to this period's fuel. The originals are kept, marked Carried.`)) return;
    setCarrying(true); setStagingMsg(null);
    try {
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'carryForwardFuel', fromPeriod: from, toPeriod: periodEnd, appliedBy: 'paul@hollypaul.com' }) });
      const json = await res.json();
      if (json.success) { setStagingMsg({ type: 'success', text: `✓ Carried ${json.carried} adjustment${json.carried !== 1 ? 's' : ''} (${fmt$(json.total)}) from ${periodLabel(from)} — confirm them below to put them on the RCTI` }); await loadStaged(); }
      else setStagingMsg({ type: 'error', text: json.error || 'Carry failed' });
    } catch (err) { setStagingMsg({ type: 'error', text: err.message }); }
    finally { setCarrying(false); }
  }

  async function confirmAll() {
    if (!clause4Active) { setConfirmMsg({ type: 'error', text: 'RTCCO clause 4 is suspended — fuel pass-through is disabled.' }); return; }
    setConfirmingAll(true); setConfirmMsg(null);
    try {
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'confirmFuelAdjustments', period: periodEnd }) });
      const json = await res.json();
      if (json.success) {
        setConfirmMsg({ type: 'success', text: `✓ ${json.confirmed} adjustment${json.confirmed !== 1 ? 's' : ''} confirmed — ready for RCTI generation`, rctiLink: true });
        await loadStaged();
      } else setConfirmMsg({ type: 'error', text: json.error || 'Confirm failed' });
    } catch (err) { setConfirmMsg({ type: 'error', text: err.message }); }
    finally { setConfirmingAll(false); }
  }

  async function pushToRCTI() {
    if (pendingCount === 0) { window.location.href = '/rcti'; return; }
    if (!clause4Active) { setConfirmMsg({ type: 'error', text: 'RTCCO clause 4 is suspended — fuel pass-through is disabled.' }); return; }
    setConfirmingAll(true); setConfirmMsg(null);
    try {
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'confirmFuelAdjustments', period: periodEnd }) });
      const json = await res.json();
      if (json.success) {
        setConfirmMsg({ type: 'success', text: `✓ ${json.confirmed} adjustment${json.confirmed !== 1 ? 's' : ''} confirmed and pushed to RCTI queue.`, rctiLink: true });
        await loadStaged();
      } else setConfirmMsg({ type: 'error', text: json.error || 'Failed' });
    } catch (err) { setConfirmMsg({ type: 'error', text: err.message }); }
    finally { setConfirmingAll(false); }
  }

  function generatePDF() {
    if (!price) { setStagingMsg({ type: 'error', text: 'Enter the AIP price before generating the PDF.' }); return; }
    const label = periodLabel(periodEnd);
    const genTime = new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const pdfRows = withKm;
    const ttlFuel     = pdfRows.reduce((s, r) => s + Math.max(0, r.fuelAdj), 0);
    const ttlGoodwill = goodwillOn ? pdfRows.reduce((s, r) => s + Math.max(0, r.goodwillAmt), 0) : 0;
    const ttlTotal    = ttlFuel + ttlGoodwill;
    const extraTh = goodwillOn ? '<th class="r">+38.4% Loading</th>' : '';
    const extraFoot = goodwillOn ? `<td class="r">+$${ttlGoodwill.toFixed(2)}</td>` : '';
    const bodyRows = pdfRows.map((r, i) => {
      const statusHtml = r.staged
        ? `<span style="padding:2px 8px;border-radius:10px;font-size:11px;font-weight:700;background:${r.staged.status==='Confirmed'?'#d1fae5':r.staged.status==='Pending'?'#fefce8':'#f3f4f6'};color:${r.staged.status==='Confirmed'?'#065f46':r.staged.status==='Pending'?'#92400e':'#6b7280'}">${r.staged.status}</span>`
        : '<span style="color:#9ca3af;font-size:11px">—</span>';
      return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
        <td><strong>${r.login}</strong></td>
        <td>${r.company||'—'}</td>
        <td class="r">${r.km} km</td>
        <td class="r">${r.l100} L/100km</td>
        <td class="r">${r.days} days</td>
        <td class="r">${r.fuelL.toFixed(1)} L</td>
        <td class="r">$${(r.fuelL*REFERENCE_PRICE).toFixed(2)}</td>
        <td class="r">$${(r.fuelL*price).toFixed(2)}</td>
        <td class="r" style="color:${r.fuelAdj>0?'#059669':'#9ca3af'};font-weight:700">${r.fuelAdj>0?'+$'+r.fuelAdj.toFixed(2):'$0.00'}</td>
        ${goodwillOn?`<td class="r" style="color:#7c3aed;font-weight:600">+$${r.goodwillAmt.toFixed(2)}</td>`:''}
        <td class="r" style="color:${r.totalAdj>0?'#064e3b':'#9ca3af'};font-weight:800">${r.totalAdj>0?'+$'+r.totalAdj.toFixed(2):'$0.00'}</td>
        <td>${statusHtml}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Fuel Assessment — ${label}</title>
<style>
  body{font-family:Arial,sans-serif;margin:32px;font-size:12px;color:#111827}
  h1{font-size:20px;color:#064e3b;margin:0 0 2px}
  .sub{font-size:12px;color:#6b7280;margin:0 0 18px}
  .meta{display:flex;gap:20px;flex-wrap:wrap;background:#f0fdf4;border:1px solid #6ee7b7;border-radius:8px;padding:12px 16px;margin-bottom:16px}
  .mi label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;display:block;margin-bottom:2px}
  .mi span{font-size:13px;font-weight:700;color:#064e3b}
  .gw{background:#f5f3ff;border:1px solid #c4b5fd;border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:11px;color:#5b21b6}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{padding:7px 9px;background:#064e3b;color:#fff;font-weight:700;white-space:nowrap}
  th.r,td.r{text-align:right}
  td{padding:6px 9px;border-bottom:1px solid #f3f4f6}
  tfoot td{background:#f0fdf4;font-weight:800;border-top:2px solid #6ee7b7;padding:8px 9px}
  .footer{margin-top:20px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af}
  @media print{body{margin:16px}}
</style></head><body>
  <h1>⛽ Fuel Assessment Report</h1>
  <p class="sub">RTCCO Fuel Cost Recovery 2026 — Variable Service Fee Adjustment — Prevailing Conditions</p>
  <div class="meta">
    <div class="mi"><label>Period</label><span>${label}</span></div>
    <div class="mi"><label>AIP Diesel Price</label><span>$${price.toFixed(3)}/L</span></div>
    <div class="mi"><label>Reference Price</label><span>$${REFERENCE_PRICE.toFixed(2)}/L</span></div>
    <div class="mi"><label>Delta</label><span style="color:${priceDelta>0?'#059669':'#dc2626'}">${priceDelta>0?'+':''}$${priceDelta.toFixed(3)}/L</span></div>
    ${windowActive?`<div class="mi"><label>High-price window</label><span>Last ${highDays} of ${PERIOD_DAYS} days (AIP > $${TRIGGER_PRICE.toFixed(2)})</span></div>`:''}
    <div class="mi"><label>Drivers</label><span>${pdfRows.length}</span></div>
    <div class="mi"><label>Total Adjustment</label><span>+$${ttlTotal.toFixed(2)}</span></div>
    <div class="mi"><label>Generated</label><span>${genTime}</span></div>
  </div>
  ${goodwillOn?`<div class="gw"><strong>Goodwill Loading +38.4% applied</strong> — Accounts for to/from job components as a goodwill gesture. This is additional to the RTCCO fuel cost recovery entitlement.</div>`:''}
  <table>
    <thead><tr>
      <th>Driver</th><th>Company</th><th class="r">KM/day</th><th class="r">L/100km</th><th class="r">Days</th>
      <th class="r">Fuel L</th><th class="r">Base @$${REFERENCE_PRICE.toFixed(2)}</th><th class="r">Current @$${price.toFixed(3)}</th>
      <th class="r">Fuel Adj</th>${extraTh}<th class="r">Total Adj</th><th>Status</th>
    </tr></thead>
    <tbody>${bodyRows}</tbody>
    <tfoot><tr>
      <td colspan="8" style="text-align:right">TOTAL</td>
      <td class="r">+$${ttlFuel.toFixed(2)}</td>${extraFoot}
      <td class="r">+$${ttlTotal.toFixed(2)}</td><td></td>
    </tr></tfoot>
  </table>
  <div class="footer">
    Hollypaul App Engine · Road Transport Contractual Chain Order — Fuel Cost Recovery 2026
    ${goodwillOn?' · 38.4% goodwill loading applied (to/from job components)':''}
  </div>
  <script>setTimeout(()=>window.print(),400)</script>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) { setStagingMsg({ type: 'error', text: 'Pop-up blocked — allow pop-ups for this site to generate the PDF.' }); return; }
    w.document.write(html); w.document.close();
  }

  return (
    <div>
      {/* Instrument banner */}
      <div style={{ background: 'linear-gradient(135deg,#064e3b 0%,#065f46 100%)', borderRadius: '12px', padding: '18px 24px', marginBottom: '20px', color: '#fff', display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6ee7b7', marginBottom: '3px' }}>Applicable Instrument</div>
          <div style={{ fontSize: '15px', fontWeight: '800', marginBottom: '2px' }}>RTCCO — Fuel Cost Recovery 2026</div>
          <div style={{ fontSize: '11px', color: '#a7f3d0' }}>Road Transport Contractual Chain Order — NSW/Federal Fuel Recovery Mechanism</div>
        </div>
        {[['Reference Price', `$${REFERENCE_PRICE.toFixed(2)}/L`], ['Default Consumption', `${DEFAULT_CONSUMPTION}L/100km`], ['Line Item', 'Variable Service Fee Adjustment']].map(([l, v]) => (
          <div key={l}><div style={{ fontSize: '10px', color: '#6ee7b7', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{l}</div><div style={{ fontSize: '12px', fontWeight: '700', marginTop: '2px' }}>{v}</div></div>
        ))}
        <button onClick={generatePDF} style={{ padding: '8px 16px', borderRadius: '7px', border: '2px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          📄 Download PDF
        </button>
      </div>

      {/* Clause 4 suspended banner */}
      {!clause4Active && (
        <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderLeft: '5px solid #f59e0b', borderRadius: '10px', padding: '14px 20px', marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>⚠️</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '800', color: '#92400e', marginBottom: '3px' }}>RTCCO clause 4 obligations SUSPENDED — fuel pass-through disabled</div>
            <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
              The Expert Panel confirmed (29 May 2026) that clause 4 no longer applies — the weekly average national terminal gate diesel price fell below $2.00/L (week ending 5 Jun 2026). Price tracking below stays live for the audit trail, but staging and pushing adjustments to RCTI is switched off. It reactivates when diesel returns above the trigger — flip <strong>clause4Active</strong> on the <a href="/mso" style={{ color: '#b45309', fontWeight: 700 }}>MSO Tracker</a>. Review checkpoint: 26 Aug 2026.
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Period */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>Period ending</div>
            <select value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', background: '#fff' }}>
              {KNOWN_PERIODS.map(p => <option key={p} value={p}>{periodLabel(p)}</option>)}
            </select>
          </div>

          {/* AIP Price */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>AIP Diesel Terminal Gate Price</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: '#374151' }}>$</span>
              <input
                type="number" step="0.001" placeholder="e.g. 1.945"
                value={aipPrice}
                onChange={e => updateAipPrice(e.target.value)}
                style={{ width: '108px', padding: '8px 10px', borderRadius: '6px', border: `2px solid ${price > REFERENCE_PRICE ? '#059669' : '#d1d5db'}`, fontSize: '15px', fontWeight: '700', color: '#111827' }}
              />
              <span style={{ fontSize: '12px', color: '#6b7280' }}>/L diesel</span>
              {price > 0 && (
                <span style={{ fontSize: '12px', fontWeight: '700', color: priceDelta > 0 ? '#059669' : '#6b7280', background: priceDelta > 0 ? '#d1fae5' : '#f3f4f6', padding: '3px 9px', borderRadius: '20px' }}>
                  {priceDelta > 0 ? '+' : ''}{priceDelta.toFixed(3)} vs ref
                </span>
              )}
              <button onClick={fetchAIPRate} disabled={aipFetching} style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', opacity: aipFetching ? 0.6 : 1 }}>
                {aipFetching ? '⏳ Fetching…' : '📡 Fetch AIP Rate'}
              </button>
            </div>
            {/* AIP fetch result */}
            {aipFetched && !aipFetchErr && (
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '6px 10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: '#065f46' }}>
                  <strong>AIP TGP ({aipFetched.city}):</strong> ${aipFetched.price.toFixed(3)}/L
                  <span style={{ color: '#6b7280', marginLeft: '6px' }}>· {new Date(aipFetched.fetchedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                </span>
                <button onClick={() => updateAipPrice(String(aipFetched.price))} style={{ padding: '3px 10px', borderRadius: '5px', border: 'none', background: '#065f46', color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                  Use AIP rate ↑
                </button>
                <a href="https://www.aip.com.au/pricing/terminal-gate-prices" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#059669' }}>View AIP ↗</a>
              </div>
            )}
            {aipFetchErr && (
              <div style={{ marginTop: '6px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', color: '#dc2626' }}>
                {aipFetchErr} — <a href="https://www.aip.com.au/pricing/terminal-gate-prices" target="_blank" rel="noopener noreferrer" style={{ color: '#dc2626' }}>open AIP site ↗</a>
              </div>
            )}
            {!aipFetched && !aipFetchErr && (
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>Enter AIP TGP, or click Fetch to pull live rate · <a href="https://www.aip.com.au/pricing/terminal-gate-prices" target="_blank" rel="noopener noreferrer" style={{ color: '#6b7280' }}>AIP site ↗</a></div>
            )}
          </div>

          {/* High-price window — days (at the end of the period) AIP sat above $2.00 */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>Days AIP above ${TRIGGER_PRICE.toFixed(2)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="number" min="1" max={PERIOD_DAYS} step="1" placeholder={`all ${PERIOD_DAYS}`}
                value={windowMap[periodEnd] || ''}
                onChange={e => updateHighDays(e.target.value)}
                style={{ width: '76px', padding: '8px 10px', borderRadius: '6px', border: `2px solid ${windowActive ? '#d97706' : '#d1d5db'}`, fontSize: '15px', fontWeight: '700', color: '#111827' }}
              />
              <span style={{ fontSize: '12px', color: '#6b7280' }}>of {PERIOD_DAYS}</span>
            </div>
            <div style={{ fontSize: '11px', color: windowActive ? '#b45309' : '#9ca3af', marginTop: '3px', maxWidth: '220px' }}>
              {windowActive
                ? `Only the last ${highDays} days count — km & days from approved shifts ${kmFrom} → ${periodEnd}`
                : 'Blank = whole period. Set N to bill only the last N high-price days.'}
            </div>
          </div>

          {/* Goodwill toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Goodwill Loading</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: goodwillOn ? '#f5f3ff' : '#f9fafb', border: `1px solid ${goodwillOn ? '#c4b5fd' : '#e5e7eb'}`, borderRadius: '8px', padding: '8px 12px' }}>
              <input type="checkbox" checked={goodwillOn} onChange={e => toggleGoodwill(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#7c3aed' }} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: goodwillOn ? '#5b21b6' : '#374151' }}>
                Apply +38.4% to/from loading
              </span>
            </label>
            {goodwillOn && <div style={{ fontSize: '11px', color: '#7c3aed', marginLeft: '2px' }}>Goodwill gesture — to/from job component</div>}
          </div>

          <button onClick={loadDrivers} disabled={loadingDrivers} style={{ padding: '9px 16px', borderRadius: '6px', border: 'none', background: '#1a3a6b', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: loadingDrivers ? 0.6 : 1, alignSelf: 'flex-end', whiteSpace: 'nowrap' }}>
            {loadingDrivers ? '⏳ Loading…' : '↻ Refresh Drivers'}
          </button>
        </div>
      </div>

      {driverError && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '14px', color: '#dc2626', marginBottom: '16px' }}>{driverError}</div>}

      {/* Summary cards */}
      {price > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '14px', marginBottom: '20px' }}>
          <SummaryCard label="AIP Price" value={`$${price.toFixed(3)}/L`} sub={priceDelta > 0 ? `+$${priceDelta.toFixed(3)} above reference` : 'At or below reference'} accent={priceDelta > 0 ? '#059669' : '#6b7280'} />
          <SummaryCard label="Drivers with Captured KM" value={withKm.length} sub={noKmCount > 0 ? `${noKmCount} driver${noKmCount!==1?'s':''} missing captured km` : 'All drivers have km'} accent="#1a3a6b" />
          <SummaryCard label="Total Adjustment" value={fmt$(totalAdj)} sub={goodwillOn ? `Includes 38.4% goodwill loading` : 'Fuel recovery only'} accent={priceDelta > 0 ? '#059669' : '#6b7280'} />
          <SummaryCard label="Confirmed" value={fmt$(confirmedTotal)} sub={`${confirmedCount} driver${confirmedCount!==1?'s':''} locked in`} accent="#7c3aed" />
        </div>
      )}

      {/* Agreed-to-pay summary */}
      {agreedCount > 0 && (
        <div style={{ background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '14px 20px', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#065f46' }}>
            Agreed to pay this period: {fmt$(agreedTotal)} · {agreedCount} driver{agreedCount !== 1 ? 's' : ''} ({pendingCount} pending, {confirmedCount} confirmed)
          </div>
          <div style={{ fontSize: '12px', color: '#059669', marginTop: '3px' }}>
            Why: RTCCO Fuel Cost Recovery 2026 — AIP ${price.toFixed(3)}/L ({priceDelta > 0 ? '+' : ''}{priceDelta.toFixed(3)} vs $1.90 ref) × route fuel litres{windowActive ? ` over the last ${highDays} days AIP sat above $${TRIGGER_PRICE.toFixed(2)}` : ''}{goodwillOn ? ' + 38.4% goodwill loading' : ''} → "Variable Service Fee Adjustment — Prevailing Conditions".
          </div>
        </div>
      )}

      {/* Pending banner */}
      {pendingCount > 0 && (
        <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '10px', padding: '14px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: '700', color: '#713f12', fontSize: '14px' }}>⚠ {pendingCount} pending fuel adjustment{pendingCount!==1?'s':''} awaiting confirmation</div>
            <div style={{ fontSize: '13px', color: '#92400e', marginTop: '2px' }}>Confirm before generating RCTIs. Confirmed entries appear as "Variable Service Fee Adjustment — Prevailing Conditions".</div>
          </div>
          {clause4Active ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={confirmAll} disabled={confirmingAll} style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#064e3b', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer', opacity: confirmingAll ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                {confirmingAll ? 'Confirming…' : '✓ Confirm All Pending'}
              </button>
              <button onClick={pushToRCTI} disabled={confirmingAll} style={{ padding: '9px 18px', borderRadius: '7px', border: '2px solid #064e3b', background: '#fff', color: '#064e3b', fontWeight: '700', fontSize: '13px', cursor: 'pointer', opacity: confirmingAll ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                Push to RCTI →
              </button>
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 700, maxWidth: '260px' }}>
              Confirm / push disabled while RTCCO clause 4 is suspended. Void these if not needed.
            </div>
          )}
        </div>
      )}

      {confirmedCount > 0 && pendingCount === 0 && !confirmMsg && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '12px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#065f46' }}>✓ {confirmedCount} adjustment{confirmedCount!==1?'s':''} confirmed for {periodLabel(periodEnd)}</span>
          <a href="/rcti" style={{ padding: '7px 16px', borderRadius: '6px', background: '#064e3b', color: '#fff', fontSize: '13px', fontWeight: '700', textDecoration: 'none', whiteSpace: 'nowrap' }}>Build RCTIs →</a>
        </div>
      )}

      <MsgBanner msg={confirmMsg} />
      <MsgBanner msg={stagingMsg} />

      {!price && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '24px', textAlign: 'center', color: '#065f46', marginBottom: '20px' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⛽</div>
          <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>Enter the AIP diesel terminal gate price above to calculate adjustments</div>
          <div style={{ fontSize: '13px', color: '#059669' }}>Click "Fetch AIP Rate" to pull the live TGP, or enter the bowser price for a higher adjustment.</div>
        </div>
      )}

      {/* Last period's unpaid fuel — offer to carry it here */}
      {prevUnpaid.length > 0 && (
        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '10px', padding: '14px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: '700', color: '#1e3a8a', fontSize: '14px' }}>{periodLabel(prevPeriodOf(periodEnd))}: {prevUnpaid.length} fuel adjustment{prevUnpaid.length !== 1 ? 's' : ''} ({fmt$(prevUnpaidTotal)}) staged but never confirmed</div>
            <div style={{ fontSize: '13px', color: '#1e40af', marginTop: '2px' }}>They never reached an RCTI. Carry them into {periodLabel(periodEnd)} to pay them on this period's RCTI as a separate catch-up line. Run the Attendance check on that period first: once carried, the amounts are fixed.</div>
          </div>
          {clause4Active && (
            <button onClick={carryForward} disabled={carrying} style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#1e40af', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer', opacity: carrying ? 0.6 : 1, whiteSpace: 'nowrap' }}>
              {carrying ? 'Carrying…' : `Carry into this period →`}
            </button>
          )}
        </div>
      )}

      {/* Catch-ups carried into this period */}
      {catchUps.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #93c5fd', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #dbeafe', background: '#eff6ff', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e3a8a' }}>Catch-up fuel from {[...new Set(catchUps.map(c => periodLabel(c.catchUpFrom)))].join(', ')}</span>
            <span style={{ fontSize: '12px', color: '#1e40af' }}>{fmt$(catchUpTotal)} · {catchUpActive.length} driver{catchUpActive.length !== 1 ? 's' : ''} · its own line on the RCTI, separate from this period's fuel</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Driver', 'Company', 'Earned in', 'Adjustment', 'Status', ''].map((h, i) => (
                    <th key={i} style={{ padding: '8px 12px', textAlign: h === 'Adjustment' ? 'right' : 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '11px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catchUps.map((c, i) => (
                  <tr key={c.rowIndex} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', opacity: c.status === 'Void' ? 0.5 : 1 }}>
                    <td style={{ padding: '8px 12px', fontWeight: '600', color: '#111827' }}>{c.driver}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{c.company || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#374151', whiteSpace: 'nowrap' }}>{periodLabel(c.catchUpFrom)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: '#064e3b' }}>{fmt$(c.adjustment)}</td>
                    <td style={{ padding: '8px 12px' }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: '8px 12px' }}>
                      {(c.status === 'Pending' || c.status === 'Confirmed') && (
                        <button onClick={() => voidAdjustment(c.rowIndex)} title="Remove this catch-up — the original goes back to Pending in its own period" style={{ padding: '4px 10px', borderRadius: '5px', border: 'none', background: '#fee2e2', color: '#dc2626', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Void</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Attendance check result */}
      {att && (
        <div style={{ background: attOver.length ? '#fef2f2' : '#f0fdf4', border: `1px solid ${attOver.length ? '#fca5a5' : '#86efac'}`, borderRadius: '10px', padding: '14px 20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: '14px', color: attOver.length ? '#991b1b' : '#065f46' }}>
                {attOver.length
                  ? `Attendance check: ${attOver.length} driver${attOver.length !== 1 ? 's are' : ' is'} paid for more days than they logged on — ${attOverDays} day${attOverDays !== 1 ? 's' : ''}, about ${fmt$(attOverAmt)}`
                  : `Attendance check: every driver's Days match the days they logged on`}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>
                Logged on = a shift log or AP scanner activity that day, {windowActive ? `over the last ${highDays} high-price days` : 'over the whole period'}.
                {att.leave === null && ' Leave calendar could not be read, so leave isn\'t shown.'}
              </div>
            </div>
            {attFixable.length > 0 && (
              <button onClick={applyAttendance} disabled={applyingAtt} style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#991b1b', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer', opacity: applyingAtt ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                {applyingAtt ? 'Applying…' : `Set Days to attended (${attFixable.length})`}
              </button>
            )}
          </div>
          {attOver.length > 0 && (
            <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {attOver.map(({ row, a }) => (
                <span key={row.login} title={a.leave.length ? `Approved leave: ${a.leave.join(', ')}` : 'No approved leave on record for the missing days'} style={{ fontSize: '12px', background: '#fff', border: '1px solid #fecaca', borderRadius: '6px', padding: '3px 8px', color: '#7f1d1d' }}>
                  <strong>{row.login}</strong> {a.paidDays}d → {a.attended}d{a.leave.length ? ` (leave ${a.leave.length}d)` : ''}{row.staged ? ` · ${row.staged.status}` : ''}
                </span>
              ))}
            </div>
          )}
          {attConfirmed.length > 0 && (
            <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '8px' }}>
              {attConfirmed.map(x => x.row.login).join(', ')} {attConfirmed.length !== 1 ? 'are' : 'is'} already Confirmed, so not changed here. Void and re-stage by hand if the RCTI hasn't been approved.
            </div>
          )}
        </div>
      )}

      {/* Driver table */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Driver Fuel Adjustments</span>
            <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '10px' }}>
              KM/day &amp; Days come from approved shift odometer logs {windowActive ? <strong style={{ color: '#b45309' }}>for the last {highDays} high-price days</strong> : 'this period'} (<span style={{ color: '#059669', fontWeight: 700 }}>● actual</span>); click any cell to override
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={runAttendanceCheck} disabled={checkingAtt} title="Check each driver's Days against the days they actually logged on (shift log or AP scanner), and show approved leave" style={{ padding: '5px 10px', borderRadius: '5px', border: '1px solid #1e40af', background: '#eff6ff', color: '#1e40af', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', opacity: checkingAtt ? 0.6 : 1 }}>
              {checkingAtt ? 'Checking…' : '✓ Attendance check'}
            </button>
            <button onClick={resetToActual} title="Clear every manual km/day override so all drivers use their captured odometer actuals" style={{ padding: '5px 10px', borderRadius: '5px', border: '1px solid #6ee7b7', background: '#ecfdf5', color: '#065f46', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ↺ Use actual km
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Set all days worked:</span>
              <input type="number" value={bulkDays} onChange={e => setBulkDays(e.target.value)} placeholder={String(windowActive ? highDays : DEFAULT_DAYS)} style={{ width: '54px', padding: '5px 7px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '12px' }} />
              <button onClick={applyDaysToAll} style={{ padding: '5px 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Apply to all</button>
            </div>
            {clause4Active && eligibleToStage.length > 0 && (
              <button onClick={stageAll} disabled={stagingAll} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#064e3b', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', opacity: stagingAll ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                {stagingAll ? 'Staging…' : `Stage All (${eligibleToStage.length})`}
              </button>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              Show drivers without captured km ({noKmCount})
            </label>
            {excludedRows.length > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#9ca3af', cursor: 'pointer' }}>
                <input type="checkbox" checked={showExcluded} onChange={e => setShowExcluded(e.target.checked)} />
                Show removed ({excludedRows.length})
              </label>
            )}
          </div>
        </div>

        {loadingDrivers ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading drivers…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Driver','Company','KM/day','L/100km','Days','Fuel L',`Base @$${REFERENCE_PRICE.toFixed(2)}`,price?`Current @$${price.toFixed(3)}`:'Current','Fuel Adj', ...(goodwillOn?['+38.4% Loading']:[]),'Total Adj','Status',''].map((h,i) => (
                    <th key={i} style={{ padding: '9px 12px', textAlign: ['Driver','Company','Status',''].includes(h)?'left':'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontSize: '11px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr><td colSpan={goodwillOn ? 14 : 13} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                    {showAll ? 'No active drivers found.' : 'No drivers with captured km yet — tick “Show drivers without captured km” to enter km manually.'}
                  </td></tr>
                ) : displayRows.map((row, i) => {
                  const canStage = clause4Active && !row.excluded && price > REFERENCE_PRICE && row.km > 0 && row.totalAdj > 0 && !row.staged;
                  return (
                    <tr key={row.login} style={{ background: row.excluded ? '#fafafa' : i % 2 === 0 ? '#fff' : '#f9fafb', opacity: row.excluded ? 0.45 : 1 }}>
                      <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' }}>{row.login}</td>
                      <td style={{ padding: '9px 12px', color: '#6b7280', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.company || '—'}</td>

                      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                        <EditCell login={row.login} value={row.km} editState={editKm} setEditState={setEditKm} onSave={saveKm} suffix=" km" step="1" minWidth="52px" />
                        {row.kmSource === 'actual' && (
                          <div title={`Actual: ${row.actualTotalKm} km over ${row.actualDays} approved day${row.actualDays !== 1 ? 's' : ''} = ${row.km} km/day avg`}
                               style={{ fontSize: '10px', color: '#059669', fontWeight: 700, marginTop: '2px' }}>● actual</div>
                        )}
                        {row.kmSource === 'manual' && (
                          <div title="Manually set — overrides captured km" style={{ fontSize: '10px', color: '#7c3aed', fontWeight: 700, marginTop: '2px' }}>✎ manual</div>
                        )}
                        {row.kmSource === 'none' && (
                          <div title="No approved odometer km captured this period — type a value to include this driver" style={{ fontSize: '10px', color: '#d97706', fontWeight: 700, marginTop: '2px' }}>⚠ no km</div>
                        )}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                        <EditCell login={row.login} value={row.l100} editState={editL100} setEditState={setEditL100} onSave={saveL100} suffix=" L" step="0.1" minWidth="46px" />
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                        <EditCell login={row.login} value={row.days} editState={editDays} setEditState={setEditDays} onSave={saveDays} suffix="d" step="1" minWidth="40px" />
                        {(() => {
                          const a = attendanceFor(row);
                          if (!a) return null;
                          return a.over > 0 ? (
                            <div title={`Paid fuel for ${a.paidDays} days; logged on ${a.attended} (shift log or AP scanner)${a.leave.length ? `; approved leave ${a.leave.join(', ')}` : ''}`} style={{ fontSize: '10px', color: '#dc2626', fontWeight: 700, marginTop: '2px', whiteSpace: 'nowrap' }}>
                              ⚠ attended {a.attended}{a.leave.length ? ` · leave ${a.leave.length}d` : ''}
                            </div>
                          ) : (
                            <div style={{ fontSize: '10px', color: '#059669', fontWeight: 700, marginTop: '2px', whiteSpace: 'nowrap' }}>✓ attended {a.attended}</div>
                          );
                        })()}
                      </td>

                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{row.km > 0 ? row.fuelL.toFixed(1) + ' L' : '—'}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#6b7280' }}>{row.km > 0 ? fmt$(row.fuelL * REFERENCE_PRICE) : '—'}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{row.km > 0 && price > 0 ? fmt$(row.fuelL * price) : '—'}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: row.fuelAdj > 0 ? '#059669' : '#9ca3af' }}>
                        {row.km > 0 && price > 0 ? (row.fuelAdj > 0 ? '+' : '') + fmt$(row.fuelAdj) : '—'}
                      </td>
                      {goodwillOn && (
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '600', color: row.goodwillAmt > 0 ? '#7c3aed' : '#9ca3af' }}>
                          {row.km > 0 && price > 0 ? '+' + fmt$(row.goodwillAmt) : '—'}
                        </td>
                      )}
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '800', color: row.totalAdj > 0 ? '#064e3b' : '#9ca3af' }}>
                        {row.km > 0 && price > 0 ? (row.totalAdj > 0 ? '+' : '') + fmt$(row.totalAdj) : '—'}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {row.staged ? <StatusBadge status={row.staged.status} /> : <span style={{ color: '#d1d5db', fontSize: '11px' }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        {row.excluded ? (
                          <button onClick={() => toggleExclude(row.login)} style={{ padding: '4px 12px', borderRadius: '5px', border: '1px solid #6ee7b7', background: '#ecfdf5', color: '#065f46', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Restore</button>
                        ) : row.staged && (row.staged.status === 'Pending' || row.staged.status === 'Confirmed') ? (
                          <button onClick={() => voidAdjustment(row.staged.rowIndex)} style={{ padding: '4px 10px', borderRadius: '5px', border: 'none', background: '#fee2e2', color: '#dc2626', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Void</button>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {canStage ? (
                              <button onClick={() => stageAdjustment(row)} disabled={stagingFor === row.login} style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: '#064e3b', color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer', opacity: stagingFor === row.login ? 0.6 : 1 }}>
                                {stagingFor === row.login ? '…' : `Stage ${fmt$(row.totalAdj)}`}
                              </button>
                            ) : row.totalAdj <= 0 && price > 0 && row.km > 0 ? (
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>No adj</span>
                            ) : null}
                            <button onClick={() => toggleExclude(row.login)} title={`Remove ${row.login} from the fuel list (restorable via "Show removed")`}
                                    style={{ padding: '4px 8px', borderRadius: '5px', border: '1px solid #e5e7eb', background: '#fff', color: '#9ca3af', fontSize: '11px', fontWeight: '700', cursor: 'pointer', lineHeight: 1 }}>✕</button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '14px 18px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '18px' }}>⛽</span>
        <div style={{ fontSize: '12px', color: '#059669' }}>
          <strong style={{ color: '#065f46' }}>Formula:</strong> (AIP − $1.90) × captured km/day × L/100km ÷ 100 × days worked{windowActive ? ` — counted over the last ${highDays} days only (AIP > $${TRIGGER_PRICE.toFixed(2)})` : ''}
          {goodwillOn && <span style={{ color: '#7c3aed' }}> + 38.4% goodwill (to/from job)</span>}.
          &nbsp; Confirmed adjustments write to <strong>Rate_Adjustments</strong> (Type=Fuel) and appear as <em>"Variable Service Fee Adjustment — Prevailing Conditions"</em> on the next RCTI.
        </div>
      </div>
    </div>
  );
}

// ── Fuel History Tab ──────────────────────────────────────────────────────────

function FuelHistoryTab() {
  const [records,      setRecords]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch('/.netlify/functions/fuel-adjustments-sb', { method: 'POST', body: JSON.stringify({ action: 'getFuelAdjustments' }) });
      const json = await res.json();
      if (json.success) setRecords(json.adjustments || []);
      else setError(json.error || 'Load failed');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const periods  = [...new Set(records.map(r => r.period).filter(Boolean))].sort().reverse();
  const filtered = records.filter(r => (!filterPeriod || r.period === filterPeriod) && (!filterStatus || r.status === filterStatus));
  const confirmedTotal = filtered.filter(r => r.status === 'Confirmed').reduce((s, r) => s + (r.adjustment || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>Period:</label>
        <select value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)} style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', color: '#374151' }}>
          <option value=''>All periods</option>
          {periods.map(p => <option key={p} value={p}>{periodLabel(p)}</option>)}
        </select>
        <label style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>Status:</label>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', color: '#374151' }}>
          <option value=''>All</option>
          {['Pending','Confirmed','Carried','Void'].map(s => <option key={s}>{s}</option>)}
        </select>
        <button onClick={load} style={{ padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#1a3a6b', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>↻ Refresh</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading fuel history…</div>}
      {error   && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '14px', color: '#dc2626', marginBottom: '20px' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '14px', marginBottom: '24px' }}>
            <SummaryCard label="Total Records"     value={filtered.length}                              sub={`across ${periods.length} period${periods.length!==1?'s':''}`}            accent="#1a3a6b" />
            <SummaryCard label="Confirmed Total"   value={fmt$(confirmedTotal)}                          sub={`${filtered.filter(r=>r.status==='Confirmed').length} confirmed`}           accent="#059669" />
            <SummaryCard label="Pending"           value={filtered.filter(r=>r.status==='Pending').length}   sub="Awaiting confirmation"                                                   accent="#d97706" />
            <SummaryCard label="Voided"            value={filtered.filter(r=>r.status==='Void').length}      sub="Cancelled entries"                                                       accent="#9ca3af" />
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280', background: '#f9fafb', borderRadius: '10px' }}>No records yet — stage and confirm adjustments in the Fuel Workbench tab.</div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Fuel Adjustment Records</span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{filtered.length} records</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {['Period','Driver','Company','AIP','Ref','KM','L/100km','Days','Fuel L','Fuel Adj','+38.4%','Total Adj','Status','Notes'].map((h,i) => (
                        <th key={i} style={{ padding: '9px 12px', textAlign: ['Period','Driver','Company','Status','Notes'].includes(h)?'left':'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontSize: '11px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, i) => (
                      <tr key={i} style={{ background: r.status==='Void' ? '#fafafa' : i%2===0 ? '#fff' : '#f9fafb', opacity: r.status==='Void' ? 0.6 : 1 }}>
                        <td style={{ padding: '9px 12px', color: '#374151', whiteSpace: 'nowrap' }}>{periodLabel(r.period)}</td>
                        <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.driver}</td>
                        <td style={{ padding: '9px 12px', color: '#6b7280' }}>{r.company || '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>${(r.aipPrice||0).toFixed(3)}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#6b7280' }}>${(r.refPrice||REFERENCE_PRICE).toFixed(2)}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{r.routeKm} km</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{r.consumption || DEFAULT_CONSUMPTION}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{r.workingDays}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{(r.fuelL||0).toFixed(1)} L</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#059669', fontWeight: '600' }}>{r.fuelAdj != null ? '+'+fmt$(r.fuelAdj) : '+'+fmt$(r.adjustment)}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#7c3aed', fontWeight: '600' }}>{r.goodwillAmt > 0 ? '+'+fmt$(r.goodwillAmt) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: r.status==='Confirmed' ? '#059669' : '#374151' }}>{(r.adjustment||0) > 0 ? '+' : ''}{fmt$(r.adjustment)}</td>
                        <td style={{ padding: '9px 12px' }}><StatusBadge status={r.status} /></td>
                        <td style={{ padding: '9px 12px', color: '#6b7280', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'workbench', label: '⛽ Fuel Workbench' },
  { id: 'history',   label: '📋 Fuel History'   },
];

export default function FuelWorkbenchPage() {
  const [activeTab, setActiveTab] = useState('workbench');
  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#064e3b', margin: '0 0 4px' }}>Fuel Workbench</h1>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>RTCCO Fuel Cost Recovery 2026 — fortnightly AIP-indexed diesel adjustment and RCTI staging</p>
      </div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: activeTab===tab.id ? '700' : '500', color: activeTab===tab.id ? '#064e3b' : '#6b7280', borderBottom: activeTab===tab.id ? '3px solid #064e3b' : '3px solid transparent', marginBottom: '-2px', transition: 'all 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'workbench' && <FuelWorkbenchTab />}
      {activeTab === 'history'   && <FuelHistoryTab />}
    </div>
  );
}
